/**
 * API Response Normalizer
 *
 * Fixes null → [] for array fields in API responses.
 * JavaScript destructuring defaults (`= []`) only work for `undefined`,
 * not `null`. When the backend returns `null` for an array field,
 * components crash on `.length`, `.map()`, `.filter()` etc.
 *
 * Usage:
 *   const data = normalizeArrayFields(await response.json(), [
 *     'deliveries', 'creditNotes', 'lines', 'goods_to_arrive_breakdown'
 *   ]);
 */

/**
 * Recursively walks an object and converts null → [] for specified field names.
 * Works at any depth, so nested objects are also fixed.
 */
export function normalizeArrayFields<T>(
  data: T,
  arrayFieldNames: string[]
): T {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => normalizeArrayFields(item, arrayFieldNames)) as T;
  }

  const fieldSet = new Set(arrayFieldNames);
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (value === null && fieldSet.has(key)) {
      result[key] = [];
    } else if (typeof value === 'object' && value !== null) {
      result[key] = normalizeArrayFields(value, arrayFieldNames);
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * Common array field names found across the wholesale API.
 * Use this as a baseline when normalizing responses.
 */
export const COMMON_ARRAY_FIELDS = [
  // Invoice hierarchy
  'deliveries',
  'creditNotes',
  'credit_notes',
  'lines',
  // Stock breakdown
  'goods_to_arrive_breakdown',
  'proposed_breakdown',
  // Orders
  'items',
  'orders',
  'shipments',
  'all_sheets',
  'discrepancies',
  'suggestions',
  'bundles',
  // Customers
  'emails',
  'websites',
  'international_marketplaces',
  'addresses',
  // General
  'results',
  'errors',
  'versions',
];
